import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/platforms", label: "Platforms" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-zinc-900/80 bg-black/80 backdrop-blur-md" data-testid="site-header">
      <div className="container-tib flex items-center justify-between h-16">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <img src="/tib-logo.png" alt="TIB" className="w-8 h-8 object-contain group-hover:scale-110 transition-transform duration-300" />
          <span className="font-mono text-sm tracking-widest uppercase text-white">
            Tech<span className="text-tib-gold">·</span>Innovation<span className="text-zinc-600">Builders</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm link-underline transition-colors ${isActive ? "text-tib-amber" : "text-zinc-400 hover:text-white"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link to="/clients/apply" data-testid="header-cta" className="btn-primary text-sm py-2.5 px-5">
            Build with TIB
          </Link>
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-900 bg-black" data-testid="mobile-menu">
          <div className="container-tib py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-zinc-300 text-lg">
                {n.label}
              </NavLink>
            ))}
            <Link to="/clients/apply" onClick={() => setOpen(false)} className="btn-primary justify-center mt-2">
              Build with TIB
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-zinc-900 bg-black" data-testid="site-footer">
      <div className="container-tib py-16">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-5">
              <img src="/tib-logo.png" alt="TIB" className="w-8 h-8 object-contain" />
              <span className="font-mono text-sm tracking-widest uppercase text-white">TechInnovationBuilders</span>
            </div>
            <p className="text-zinc-500 max-w-md leading-relaxed">
              The build engine for <span className="text-zinc-300">MPGBusinessFunding.com</span>. We design, ship, own, and
              operate platforms — and build them under contract for external clients.
            </p>
            <p className="mt-4 text-sm text-zinc-400">
              Call or text <a href="tel:+18887888450" className="text-tib-gold hover:text-tib-amber" data-testid="footer-phone">(888) 788-8450</a>
            </p>
          </div>
          <div>
            <div className="overline mb-5">Platform</div>
            <ul className="space-y-3 text-sm text-zinc-400">
              <li><Link to="/platforms" className="hover:text-tib-amber">Portfolio</Link></li>
              <li><Link to="/services" className="hover:text-tib-amber">Services</Link></li>
              <li><Link to="/case-studies" className="hover:text-tib-amber">Case Studies</Link></li>
              <li><Link to="/clients/apply" className="hover:text-tib-amber">Apply to Build</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-5">Company</div>
            <ul className="space-y-3 text-sm text-zinc-400">
              <li><Link to="/about" className="hover:text-tib-amber">About</Link></li>
              <li><Link to="/contact" className="hover:text-tib-amber">Contact</Link></li>
              <li><a href="/legal/privacy" className="hover:text-tib-amber">Privacy</a></li>
              <li><a href="/legal/terms" className="hover:text-tib-amber">Terms</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-zinc-900 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-600">
            © {year} TechInnovationBuilders.com, LLC — owned by MPGBusinessFunding.com, LLC.
          </p>
          <a
            href="https://ideadepot.ai?ref=tib&utm_source=tib&utm_medium=footer"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-visit-ideadepot"
            className="text-xs text-zinc-600 hover:text-tib-mint transition-colors"
          >
            Visit IdeaDepot.ai →
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen flex flex-col bg-black">
      <Header key={pathname} />
      <main className="flex-1 pt-16">{children}</main>
      <Footer />
    </div>
  );
}
